
import { N8N_CONFIG } from "../constants";
import { LEAD_USER_MESSAGES, type LeadSubmitResult } from "./leadTypes";

interface WebsiteLeadWebhookResponse {
  success?: boolean;
  pbId?: string;
  emailSent?: boolean;
  emailError?: string | null;
  duplicate?: boolean;
  blocked?: boolean;
  message?: string;
}

function mapWebsiteLeadResponse(data: WebsiteLeadWebhookResponse | null): LeadSubmitResult {
  if (data?.blocked || data?.duplicate) {
    return {
      ok: false,
      status: 'error',
      blocked: true,
      message: data.message || 'Anfrage bereits gesendet.',
    };
  }

  if (!data?.success) {
    return {
      ok: false,
      status: 'error',
      message: data?.message || LEAD_USER_MESSAGES.webhookError,
    };
  }

  const pbSaved = Boolean(data.pbId);
  const emailSent = data.emailSent === true;

  if (pbSaved && emailSent) {
    return {
      ok: true,
      status: 'success',
      message: LEAD_USER_MESSAGES.success,
      emailSent: true,
      pbSaved: true,
      pbId: data.pbId,
    };
  }

  if (pbSaved) {
    return {
      ok: true,
      status: 'partial',
      message: LEAD_USER_MESSAGES.partial,
      emailSent: false,
      pbSaved: true,
      pbId: data.pbId,
    };
  }

  return {
    ok: false,
    status: 'error',
    message: data?.message || LEAD_USER_MESSAGES.webhookError,
  };
}

export interface HealthCheckResult {
    status: 'ONLINE' | 'MISSING_WORKFLOW' | 'SERVER_ERROR' | 'OFFLINE';
    code: number;
    latency: number;
    message: string;
    solution?: string;
    serviceName?: string;
    version?: string; // NEU: Echte Version vom Server
}

export const sendAdminNotification = async (type: string, payload: any): Promise<LeadSubmitResult> => {
  if (type !== 'LEAD') {
    console.log(`[Admin Log] ${type}:`, payload);
    return { ok: true };
  }

  const endpoint =
    (import.meta.env.VITE_WEBSITE_LEAD_WEBHOOK as string | undefined)?.trim() ||
    N8N_CONFIG.WEBSITE_LEAD_WEBHOOK;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type,
        ...payload,
        submittedAt: new Date().toISOString(),
      }),
    });

    if (response.status === 429) {
      return {
        ok: false,
        blocked: true,
        message: 'Zu viele Anfragen in kurzer Zeit. Bitte später erneut versuchen.',
      };
    }

    if (!response.ok) {
      console.error('[Lead] Webhook failed', response.status);
      let data: WebsiteLeadWebhookResponse | null = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (data) {
        const mapped = mapWebsiteLeadResponse(data);
        if (!mapped.ok) {
          return mapped;
        }
      }

      return {
        ok: false,
        status: 'error',
        message: LEAD_USER_MESSAGES.networkError,
      };
    }

    let data: WebsiteLeadWebhookResponse | null = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    return mapWebsiteLeadResponse(data);
  } catch (error) {
    console.error('[Lead] Webhook error', error);
    return {
      ok: false,
      status: 'error',
      message: LEAD_USER_MESSAGES.networkError,
    };
  }
};

export const triggerWorkflow = async (workflowId: string, data: any) => {
    const endpoint = workflowId.startsWith('/') ? workflowId : `/webhook/${workflowId}`;
    try {
        await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
    } catch (e) {
         console.error("Workflow trigger failed", e);
    }
};

export const sendChatToBot = async (
  message: string,
  history: any[],
  options?: { intent?: string },
) => {
    // Sales-ARKO (Landing) hat seit dem Split (10.07.2026) einen eigenen Workflow
    // SALES_ARKO_AGENT.json unter /webhook/sales-arko. Portal-ARKO bleibt /webhook/arko.
    const url = N8N_CONFIG.SALES_BOT_WEBHOOK;
    console.log(`[Bot Request] Sende an ${url}...`);

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                chatInput: message,
                history: history,
                mode: "frontend_client",
                ...(options?.intent ? { intent: options.intent } : {}),
            })
        });

        if (!response.ok) {
            throw new Error(`Server Error: ${response.status}`);
        }

        const data = await response.json();
        const botText = data.output || data.text || data.message || data.content || JSON.stringify(data);
        return botText;

    } catch (error) {
        console.error("Bot Error:", error);
          return "ARKO ist gerade kurz beschäftigt. Bitte nochmals versuchen oder schreiben Sie direkt an office@arkorbit.at.";
    }
};

// GENERIC SERVICE CHECKER (REAL PING & METADATA)
export const checkServiceHealth = async (url: string, name: string): Promise<HealthCheckResult> => {
    const start = Date.now();
    try {
        // Nextcloud Status via internem Proxy abrufen, um JSON zu parsen
        // Bei n8n Webhook nutzen wir POST
        const isWebhook = url.includes('webhook');
        const method = isWebhook ? 'POST' : 'GET';
        
        const response = await fetch(url, {
            method: method, 
            headers: { 
                'Content-Type': 'application/json',
                'Accept': 'application/json' 
            },
            body: isWebhook ? JSON.stringify({ check: true }) : undefined,
            signal: AbortSignal.timeout(5000) 
        });
        
        const latency = Date.now() - start;
        const code = response.status;
        let detectedVersion = undefined;

        // VERSUCHE ECHTE VERSION ZU LESEN (JSON)
        if (response.ok) {
            try {
                const jsonData = await response.json();
                
                // Nextcloud Status.php Format
                if (jsonData.versionstring) {
                    detectedVersion = `v${jsonData.versionstring}`;
                } 
                // Nextcloud Alternativ
                else if (jsonData.nextcloud && jsonData.nextcloud.system && jsonData.nextcloud.system.version) {
                    detectedVersion = `v${jsonData.nextcloud.system.version}`;
                }
            } catch (e) {
                // Kein JSON, ist okay.
            }
        }

        // 200-299 = OK
        if (code >= 200 && code < 300) {
            return { status: 'ONLINE', code, latency, message: "OK", serviceName: name, version: detectedVersion };
        }
        
        // 500er bei n8n oft "ok" (weil Workflow läuft aber keinen Input hat)
        if (code === 500 || code === 401 || code === 403) {
             return { status: 'ONLINE', code, latency, message: `Active (${code})`, serviceName: name };
        }

        // 404
        if (code === 404) {
            return { status: 'MISSING_WORKFLOW', code, latency, message: "Endpoint 404", serviceName: name };
        }

        return { status: 'OFFLINE', code, latency, message: `Error ${code}`, serviceName: name };

    } catch (e) {
        return { status: 'OFFLINE', code: 0, latency: 0, message: "Unreachable", serviceName: name };
    }
};

export const checkServerConnection = async (url: string = N8N_CONFIG.SALES_BOT_WEBHOOK): Promise<HealthCheckResult> => {
    return checkServiceHealth(url, "n8n Core");
};
